<script setup lang="ts">
const emit = defineEmits(["close", "submitAccept", "error"]);
const props = defineProps({
  offer: {
    type: Object as PropType<Offer>,
    required: true,
  },
});

const { marketplaceFeesPercent, minimumPrice } = useRuntimeConfig().public;
const GFALExStore = useGFALExStore();
const isLoading = ref(false);
const isAboveMinimumFee = ref(false);
const minimumFee = ref("");

onMounted(() => {
  if (!Number(marketplaceFeesPercent)) {
    emit("error");
  }
});

const amountAfterFee = computed(() => {
  if (!marketplaceFeesPercent) {
    emit("error");
    return;
  }
  const minimumPriceGFAL = Number(minimumPrice) / GFALExStore.GFALExRate;
  const marketplaceFee =
    Number(props.offer.amount.GFAL) * (Number(marketplaceFeesPercent) / 100);
  if (marketplaceFee > minimumPriceGFAL) {
    isAboveMinimumFee.value = true;
    return roundNum(Number(props.offer.amount.GFAL) - Number(marketplaceFee));
  } else {
    isAboveMinimumFee.value = false;
    minimumFee.value = roundNum(minimumPriceGFAL);
    return roundNum(Number(props.offer.amount.GFAL) - Number(minimumPriceGFAL));
  }
});

const acceptOffer = () => {
  isLoading.value = true;
  setTimeout(() => {
    emit("submitAccept");
  }, 500);
};
</script>
<template>
  <div class="flex flex-col items-center gap-y-6 mt-8 mb-4">
    <img
      class="w-32 object-contain border border-secondary-500"
      :src="getPortraitItemImage(offer.imageId)"
      @error="
        (e) => ((e.target as HTMLImageElement).src = getPortraitItemImage())
      " />
    <i18n-t
      tag="p"
      class="text-neutral-300 text-center max-w-xs px-2"
      keypath="main.modalOffer.offerAcceptDetail"
      scope="global">
      <template #amount>
        <span class="font-bold text-neutral-200"
          >{{ roundNum(offer.amount.GFAL) }} GFAL</span
        >
      </template>
      <template #collectible>
        <span class="font-bold text-neutral-200">{{
          $t(`${offer.translationId}.title`) +
          " " +
          $t(`${offer.translationId}.subtitle`) +
          " #" +
          offer.collectibleNumber
        }}</span>
      </template>
    </i18n-t>
    <i18n-t
      tag="p"
      class="text-neutral-300 text-center text-xs"
      keypath="main.modalOffer.offerAcceptFee"
      scope="global">
      <template #amount>
        <span class="font-bold text-neutral-200"
          >{{ amountAfterFee }} GFAL</span
        >
      </template>
      <template #fee>
        <span class="font-bold text-neutral-200">{{
          $t("main.modalOffer.fee", {
            fee: isAboveMinimumFee
              ? Number(marketplaceFeesPercent) + "%"
              : minimumFee + " GFAL",
          })
        }}</span>
      </template>
    </i18n-t>
    <div class="w-full mt-auto mb-2 flex justify-center items-center gap-4">
      <BaseButtonSubmit
        :loading="isLoading"
        :text="$t('main.modalOffer.acceptOffer')"
        :disabled="isLoading"
        data-test="button-accept-offer"
        :contextual-styles="true"
        @click="acceptOffer" />
      <BaseButtonCancel
        :text="$t('main.modalOffer.cancel')"
        data-test="button-dismiss-accept-offer"
        :contextual-styles="true"
        @click="$emit('close')" />
    </div>
  </div>
</template>
